/**
 * ProtectedRoute — wraps any route that requires the user to be:
 *
 *   1. Authenticated (valid, unexpired JWT in the auth store)
 *   2. Holding one of the `allowRoles` (default: only PATIENT)
 *
 * Otherwise bounces to sign-in — /start (mobile sign-in) for patient-only
 * pages, /login (email) for everything else. Roles not in the allow-list are refused
 * with a friendly toast so e.g. a clinician who lands on the patient
 * kiosk URL — or a patient who lands on the employer dashboard — isn't
 * silently denied.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useEffect } from 'react';
import type { UserRole } from '@cardioq/engine-types';
import { useAuth } from '@/hooks/useAuth';
import { PATIENT_SIGN_IN, STAFF_SIGN_IN } from '@/lib/signInPath';

export interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Roles allowed on this route (default: only PATIENT). */
  allowRoles?: ReadonlyArray<UserRole>;
  /** What to call this area in the rejection toast (default: "patients"). */
  audienceLabel?: string;
}

export function ProtectedRoute({
  children,
  allowRoles = ['PATIENT'],
  audienceLabel = 'patients',
}: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, role, isExpired } = useAuth();

  const allowed = role != null && allowRoles.includes(role);

  // Show role-rejected toast once when a signed-in user lands on a route
  // their role can't access.
  useEffect(() => {
    if (isAuthenticated && !isExpired && role && !allowed) {
      toast.error(
        `This area is for ${audienceLabel}. You're signed in as ${role}.`,
        { id: 'role-rejected' },
      );
    }
  }, [isAuthenticated, isExpired, role, allowed, audienceLabel]);

  if (!isAuthenticated || isExpired) {
    const patientOnly = allowRoles.length === 1 && allowRoles[0] === 'PATIENT';
    return (
      <Navigate
        to={patientOnly ? PATIENT_SIGN_IN : STAFF_SIGN_IN}
        replace
        state={{ from: location }}
      />
    );
  }

  if (role && !allowed) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
